import { Switch } from '../../../shared/Switch';
import { EditorFieldError } from '../../shared/editor-page/EditorFieldError';
import { GiftPassBundlePromotedBadge } from '../components/GiftPassBundlePromotedBadge';

import type { BundleFormErrors, BundleFormValues } from './bundle-form';

import './gift-pass-bundle-editor.css';

export interface BundleStatusCardProps {
  values: BundleFormValues;
  errors: BundleFormErrors;
  isNew: boolean;
  /** Whether the bundle is promoted as saved, restored if it is switched off and back on. */
  savedPromoted: boolean;
  /** The name of another bundle that is promoted now, if any. */
  promotedElsewhere: string | null;
  onChange: (patch: Partial<Pick<BundleFormValues, 'isActive' | 'isPromoted'>>) => void;
}

function promotedHint(values: BundleFormValues, promotedElsewhere: string | null) {
  if (!values.isActive) return 'Enable this bundle to promote it.';
  if (values.isPromoted && promotedElsewhere) {
    return (
      <>
        Saving takes the promotion from <strong>{promotedElsewhere}</strong>. Only one bundle can be
        promoted at a time.
      </>
    );
  }
  if (values.isPromoted) {
    return 'The featured bundle on the purchase page: its Grab button is filled green.';
  }
  if (promotedElsewhere) {
    return (
      <>
        <strong>{promotedElsewhere}</strong> is promoted now. Only one bundle can be promoted at a
        time.
      </>
    );
  }
  return 'Feature this bundle on the purchase page by filling its Grab button green.';
}

export function BundleStatusCard(p: BundleStatusCardProps) {
  const { values } = p;
  return (
    <section className="nb-card gpb-ed-status" aria-label="Bundle status">
      {!p.isNew && (
        <div className="gpb-ed-switch-row">
          <div>
            <div className="gpb-ed-switch-title">{values.isActive ? 'Enabled' : 'Disabled'}</div>
            <div id="bundle-active-hint" className="gpb-ed-switch-hint">
              {values.isActive
                ? 'Customers can buy this bundle on the purchase page.'
                : 'Hidden from the purchase page. Passes already sold keep their value.'}
            </div>
          </div>
          <Switch
            checked={values.isActive}
            aria-label="Enabled"
            aria-describedby="bundle-active-hint"
            onChange={(isActive) =>
              p.onChange({ isActive, isPromoted: isActive && p.savedPromoted })
            }
          />
        </div>
      )}
      <div className="gpb-ed-switch-row" data-muted={!values.isActive}>
        <div>
          <div className="gpb-ed-switch-title">
            Promoted {values.isPromoted && <GiftPassBundlePromotedBadge />}
          </div>
          <div id="bundle-promoted-hint" className="gpb-ed-switch-hint">
            {promotedHint(values, p.promotedElsewhere)}
          </div>
        </div>
        <Switch
          checked={values.isPromoted}
          disabled={!values.isActive}
          aria-label="Promoted"
          aria-describedby="bundle-promoted-hint"
          onChange={(isPromoted) => p.onChange({ isPromoted })}
        />
      </div>
      {p.errors.isActive && (
        <EditorFieldError id="bundle-active-error">{p.errors.isActive}</EditorFieldError>
      )}
      {p.errors.isPromoted && (
        <EditorFieldError id="bundle-promoted-error">{p.errors.isPromoted}</EditorFieldError>
      )}
    </section>
  );
}
